/**
 * PostHog analytics utility
 * Provides type-safe event tracking functions
 */

import { devLog } from '@/lib/posthog/dev-log';

/**
 * Internal user email domains/patterns to filter from analytics
 * These users' events will be filtered out to avoid skewing analytics
 */
const INTERNAL_USER_PATTERNS = [
  '@emailferret.io',
  '@test.com',
  '+test@', // Email aliases like user+test@example.com
];

/**
 * Check if an email belongs to an internal/test user
 * @param email - Email address to check
 * @returns true if email matches internal user patterns
 */
export function isInternalUser(email?: string | null): boolean {
  if (!email) return false;
  return INTERNAL_USER_PATTERNS.some((pattern) => email.includes(pattern));
}

import type { PostHogInstance } from '@/lib/posthog/types';

/**
 * Get PostHog instance from window (initialized in PostHogProvider component)
 * PostHog attaches itself to window.posthog when initialized
 * @returns PostHog instance or null if not available
 */
function getPostHog(): PostHogInstance | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // PostHog attaches itself to window.posthog when initialized
    const posthogWindow = window as import('@/lib/posthog/types').PostHogWindow;
    const ph = posthogWindow.posthog;

    // Check if PostHog instance exists and has capture method (indicates it's ready)
    if (ph && typeof ph.capture === 'function') {
      return ph;
    }

    return null;
  } catch (error) {
    devLog('warn', 'PostHog not available', { error });
    return null;
  }
}

/**
 * Enable PostHog features for logged-in users
 * Enables autocapture and starts session recording
 * @param posthog - PostHog instance
 * @param userId - User ID for logging
 */
function enablePostHogFeatures(posthog: PostHogInstance, userId: string): void {
  // Enable autocapture for logged-in users (tracks clicks, form submissions, etc.)
  // This complements our custom business events with UI interaction data
  if (posthog.config) {
    posthog.config.autocapture = true;
    devLog('debug', 'PostHog autocapture enabled', { userId });
  }

  // Start session recording for logged-in users
  if (typeof posthog.startSessionRecording === 'function') {
    posthog.startSessionRecording();
    devLog('debug', 'PostHog session recording started', { userId });
  }
}

/**
 * Disable PostHog features when user logs out
 * Disables autocapture and stops session recording
 * @param posthog - PostHog instance
 */
function disablePostHogFeatures(posthog: PostHogInstance): void {
  // Disable autocapture when user logs out
  if (posthog.config) {
    posthog.config.autocapture = false;
    devLog('debug', 'PostHog autocapture disabled');
  }

  // Stop session recording when user logs out
  if (typeof posthog.stopSessionRecording === 'function') {
    posthog.stopSessionRecording();
    devLog('debug', 'PostHog session recording stopped');
  }
}

/**
 * Collect standard properties that should be attached to every custom event.
 * Runs only in the browser and silently returns an empty object on SSR or
 * when `window.location` isn't available (e.g. jsdom without a location stub).
 */
function getStandardEventProperties(): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const path = window.location?.pathname;
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    return {
      ...(path ? { path } : {}),
      ...(referrer ? { referrer } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * Track a custom event
 * Automatically filters out events from internal/test users and enriches
 * every event with standard page context (path, referrer).
 * @param eventName - Name of the event
 * @param properties - Optional event properties
 * @example
 * ```ts
 * trackEvent('button_clicked', { button_name: 'Submit', page: 'Settings' });
 * ```
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  const posthog = getPostHog();
  if (!posthog) {
    devLog('warn', 'PostHog not available for tracking', { eventName });
    return;
  }

  // Filter out events from internal users (check email in properties or user identification)
  const email = typeof properties?.email === 'string' ? properties.email : undefined;
  if (isInternalUser(email)) {
    devLog('debug', 'PostHog event filtered (internal user)', { eventName, email });
    return;
  }

  const enrichedProperties = {
    ...getStandardEventProperties(),
    ...properties,
  };

  try {
    posthog.capture(eventName, enrichedProperties);
    devLog('debug', 'PostHog event tracked', { eventName, properties: enrichedProperties });
  } catch (error) {
    // Silently fail if tracking fails
    devLog('warn', 'PostHog tracking failed', {
      eventName,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Identify a user and enable PostHog features for logged-in users
 * Enables autocapture and session recording automatically
 * Filters out internal/test users from analytics
 * @param userId - User ID
 * @param properties - Optional user properties (should include email for filtering)
 * @example
 * ```ts
 * identifyUser('user-123', {
 *   email: 'user@example.com',
 *   subscription_tier: 'monthly',
 *   subscription_status: 'active'
 * });
 * ```
 * @see {@link resetUser} - Reset user identification on logout
 * @see {@link trackEvent} - Track custom events for identified users
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  const posthog = getPostHog();
  if (!posthog) {
    devLog('warn', 'PostHog not available for identify');
    return;
  }

  // Filter out internal users from identification
  const email = typeof properties?.email === 'string' ? properties.email : undefined;
  if (isInternalUser(email)) {
    devLog('debug', 'PostHog user identification skipped (internal user)', { userId, email });
    return;
  }

  try {
    posthog.identify(userId, properties);
    enablePostHogFeatures(posthog, userId);
    devLog('debug', 'PostHog user identified', { userId, properties });
  } catch (error) {
    // Silently fail if identification fails
    devLog('warn', 'PostHog identify failed', {
      userId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Reset user identification and disable PostHog features (on logout)
 * Disables autocapture and stops session recording
 * @example
 * ```ts
 * resetUser(); // Call on logout
 * ```
 * @see {@link identifyUser} - Identify user on login
 */
export function resetUser(): void {
  const posthog = getPostHog();
  if (!posthog) {
    return;
  }

  try {
    disablePostHogFeatures(posthog);
    posthog.reset();
  } catch (error) {
    // Silently fail if reset fails
    devLog('warn', 'PostHog reset failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Track Gmail account connected.
 * Also updates the PostHog person profile in-band via `$set` so properties
 * like `email`, `gmail_connected`, and `first_gmail_connected_at` are
 * refreshed immediately without waiting for the next identify call.
 *
 * @param email - Gmail account email
 * @param isPrimary - Whether this is the primary account
 * @example
 * ```ts
 * trackGmailConnected('user@gmail.com', true);
 * ```
 */
export function trackGmailConnected(email: string, isPrimary: boolean = false): void {
  trackEvent('gmail_connected', {
    email,
    is_primary: isPrimary,
    // `$set` is PostHog's in-event person-properties update: the server will
    // merge these keys into the current person profile when the event lands.
    $set: {
      email,
      gmail_connected: true,
    },
    $set_once: {
      first_gmail_connected_at: new Date().toISOString(),
    },
  });
}

/**
 * Track Gmail account disconnected
 * @param email - Gmail account email
 * @example
 * ```ts
 * trackGmailDisconnected('user@gmail.com');
 * ```
 */
export function trackGmailDisconnected(email: string): void {
  trackEvent('gmail_disconnected', {
    email,
  });
}

/**
 * Track settings updated
 * @param settings - Settings that were updated
 * @example
 * ```ts
 * trackSettingsUpdated({
 *   threshold: 5,
 *   action: 'label',
 *   label_name: 'Suspected AI Outreach'
 * });
 * ```
 */
export function trackSettingsUpdated(settings: {
  threshold?: number;
  action?: string;
  label_name?: string;
}): void {
  trackEvent('settings_updated', settings);
}

/**
 * Track allowlist entry added
 * @param domain - Domain pattern added to allowlist
 * @example
 * ```ts
 * trackAllowlistAdded('example.com');
 * ```
 */
export function trackAllowlistAdded(domain: string): void {
  trackEvent('allowlist_added', {
    domain,
  });
}

/**
 * Track allowlist entry removed
 * @param domain - Domain pattern removed from allowlist
 * @example
 * ```ts
 * trackAllowlistRemoved('example.com');
 * ```
 */
export function trackAllowlistRemoved(domain: string): void {
  trackEvent('allowlist_removed', {
    domain,
  });
}

/**
 * Track blocklist entry added
 * @param domain - Domain pattern added to blocklist
 * @example
 * ```ts
 * trackBlocklistAdded('spam.com');
 * ```
 */
export function trackBlocklistAdded(domain: string): void {
  trackEvent('blocklist_added', {
    domain,
  });
}

/**
 * Track blocklist entry removed
 * @param domain - Domain pattern removed from blocklist
 * @example
 * ```ts
 * trackBlocklistRemoved('spam.com');
 * ```
 */
export function trackBlocklistRemoved(domain: string): void {
  trackEvent('blocklist_removed', {
    domain,
  });
}

/**
 * Track subscription started
 * @param tier - Subscription tier (e.g., 'monthly', 'yearly')
 * @param priceId - Stripe price ID
 * @example
 * ```ts
 * trackSubscriptionStarted('monthly', 'price_1234567890');
 * ```
 */
export function trackSubscriptionStarted(tier: string, priceId: string): void {
  trackEvent('subscription_started', {
    tier,
    price_id: priceId,
  });
}

/**
 * Track subscription canceled
 * @param tier - Subscription tier that was canceled
 * @example
 * ```ts
 * trackSubscriptionCanceled('monthly');
 * ```
 */
export function trackSubscriptionCanceled(tier: string): void {
  trackEvent('subscription_canceled', {
    tier,
  });
}

/**
 * Track checkout initiated
 * @param tier - Subscription tier user is checking out for
 * @example
 * ```ts
 * trackCheckoutInitiated('monthly');
 * ```
 */
export function trackCheckoutInitiated(tier: string): void {
  trackEvent('checkout_initiated', {
    tier,
  });
}

/**
 * Track checkout completed
 * @param tier - Subscription tier that was purchased
 * @example
 * ```ts
 * trackCheckoutCompleted('monthly');
 * ```
 */
export function trackCheckoutCompleted(tier: string): void {
  trackEvent('checkout_completed', {
    tier,
  });
}

/**
 * Track checkout canceled
 * @example
 * ```ts
 * trackCheckoutCanceled();
 * ```
 */
export function trackCheckoutCanceled(): void {
  trackEvent('checkout_canceled');
}

/**
 * Track message viewed
 * @param messageId - Gmail message ID
 * @example
 * ```ts
 * trackMessageViewed('18c1234567890abcdef');
 * ```
 */
export function trackMessageViewed(messageId: string): void {
  trackEvent('message_viewed', {
    message_id: messageId,
  });
}

/**
 * Properties describing a marketing/CTA click.
 */
export interface CtaClickedProperties {
  /** Where on the page the CTA lives, e.g. 'hero', 'mid_cta', 'final_cta', 'nav'. */
  location: string;
  /** Where the click is sending the user, e.g. '/login', '/features'. */
  destination: string;
  /** Which page the click originated from, e.g. 'landing', 'pricing', 'features'. */
  page: string;
  /** Optional button label / visible text. */
  label?: string;
}

/**
 * Track a marketing CTA click.
 * Used as the conversion-intent signal between a $pageview and the downstream
 * login / signup / checkout events so we can build a full acquisition funnel.
 */
export function trackCtaClicked(props: CtaClickedProperties): void {
  trackEvent('cta_clicked', { ...props });
}

/**
 * Track that a specific section of a marketing page has scrolled into view.
 * Fired at most once per section per page load by useSectionViewTracking.
 */
export function trackSectionViewed(section: string, page: string): void {
  trackEvent('section_viewed', { section, page });
}

/**
 * Track that the user has initiated login/sign-up via an auth provider.
 * Fires before the OAuth redirect so it is recorded even when the user never
 * returns.
 */
export function trackLoginInitiated(method: 'google' = 'google'): void {
  trackEvent('login_initiated', { method });
}

/**
 * Track that a brand-new user has successfully completed signup.
 * Callers are responsible for deciding whether the authenticated user is
 * first-time (e.g. comparing `user.created_at` to `Date.now()`).
 */
export function trackSignupCompleted(method: 'google' = 'google'): void {
  trackEvent('signup_completed', { method });
}

/**
 * Track that the onboarding page has been viewed.
 */
export function trackOnboardingViewed(): void {
  trackEvent('onboarding_viewed');
}

/**
 * Track that the user has clicked the Gmail connect button during onboarding.
 * Complements `gmail_connected`, which fires only on successful OAuth return.
 */
export function trackGmailConnectClicked(): void {
  trackEvent('gmail_connect_clicked');
}

/**
 * Track that the checkout page has been viewed.
 * @param plan - Plan the user is checking out for (e.g. 'monthly').
 * @param trialExpired - Whether the user's trial has already expired.
 */
export function trackCheckoutPageViewed(plan: string, trialExpired: boolean): void {
  trackEvent('checkout_page_viewed', { plan, trial_expired: trialExpired });
}

/**
 * Track that the user has submitted the checkout form (before Stripe
 * confirmation). Used to measure drop-off between form-view and form-submit.
 */
export function trackCheckoutSubmitted(plan: string): void {
  trackEvent('checkout_submitted', { plan });
}
